import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Settings,
  Brain,
  Shield,
  Lock,
  FileText,
  Search,
  Image,
  Palette,
  Check,
  X,
  Edit2
} from 'lucide-react'
import { adminApi } from '@/lib/api'

interface PlatformSetting {
  key: string
  value: string | null
  description: string | null
  createdAt: string
  updatedAt: string
}

interface LLMModel {
  id: string
  name: string
  provider: string
  description: string
}

// One resolution from the backend's own wire-format resolver.
interface WireFormatExplanation {
  format: 'xml' | 'mcp'
  reason: 'platform-disabled' | 'no-function-calling' | 'no-tool-streaming' | 'native'
  supportsToolCalls: boolean
  supportsToolStreaming: boolean
  toolCallsSource: 'db' | 'inferred'
  toolStreamingSource: 'db' | 'inferred'
}

interface ModelWireFormat {
  id: string
  serviceIdentifier: string
  name: string
  provider: string
  withStreamingTools: WireFormatExplanation
  withoutStreamingTools: WireFormatExplanation
}

const WIRE_FORMAT_REASON_LABEL: Record<WireFormatExplanation['reason'], string> = {
  'platform-disabled': 'native tool calls disabled',
  'no-function-calling': 'no function calling',
  'no-tool-streaming': 'no tool streaming',
  native: 'native',
}

interface ImageModel {
  serviceIdentifier: string
  displayName: string
  serviceType: string
  costUsdPerUnit: number | null
  creditsPerAction: number
}

interface ImageStyle {
  id: string
  name: string
  description?: string
  stylePrompt: string
  negativePrompt?: string
  preferredModel?: string
  enabled: boolean
  order: number
}

export function PlatformSettingsPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<Record<string, PlatformSetting>>({})
  const [availableModels, setAvailableModels] = useState<LLMModel[]>([])
  const [availableToolModels, setAvailableToolModels] = useState<LLMModel[]>([])
  const [defaultModel, setDefaultModel] = useState('')
  const [defaultToolModel, setDefaultToolModel] = useState('')
  const [globalSystemPromptValue, setGlobalSystemPromptValue] = useState('')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  // null = not loaded (or the endpoint failed); the section then stays hidden.
  const [wireFormats, setWireFormats] = useState<ModelWireFormat[] | null>(null)

  // Image model settings
  const [availableImageModels, setAvailableImageModels] = useState<ImageModel[]>([])
  const [defaultImageModel, setDefaultImageModel] = useState('')
  const [defaultImageToImageModel, setDefaultImageToImageModel] = useState('')

  // Image styles
  const [imageStyles, setImageStyles] = useState<ImageStyle[]>([])
  const [editingStyle, setEditingStyle] = useState<ImageStyle | null>(null)

  // Load platform settings
  const loadSettings = async () => {
    try {
      setLoading(true)
      const [settingsData, llmData, imageData, stylesData, wireFormatData] = await Promise.all([
        adminApi.getPlatformSettings(),
        adminApi.getLLMModelSettings(),
        adminApi.getImageModelSettings().catch(() => ({ availableModels: [], defaultImageModel: null, defaultImageToImageModel: null })),
        adminApi.getImageStyles().catch(() => ({ styles: [] })),
        adminApi.getToolWireFormats().catch(() => null)
      ])
      setWireFormats(wireFormatData?.models ?? null)

      // Convert array to keyed object for easier access
      const settingsMap = settingsData.reduce((acc: Record<string, PlatformSetting>, setting: PlatformSetting) => {
        acc[setting.key] = setting
        return acc
      }, {})

      setSettings(settingsMap)
      setAvailableModels(llmData.availableModels || [])
      setAvailableToolModels(llmData.availableToolModels || [])
      setDefaultModel(llmData.defaultModel || '')
      setDefaultToolModel(llmData.defaultToolModel || '')

      // Set image model settings
      setAvailableImageModels(imageData.availableModels || [])
      setDefaultImageModel(imageData.defaultImageModel || '')
      setDefaultImageToImageModel(imageData.defaultImageToImageModel || '')

      // Set image styles
      setImageStyles(stylesData.styles || [])

      // Set global system prompt value
      if (settingsMap.globalSystemPrompt?.value) {
        setGlobalSystemPromptValue(settingsMap.globalSystemPrompt.value)
      }
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Failed to load platform settings:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSettings()
  }, [])

  // Update a specific setting
  const updateSetting = async (key: string, value: string) => {
    try {
      setSaving(true)
      await adminApi.updatePlatformSetting(key, value)
      await loadSettings() // Reload to get updated timestamps
    } catch (error) {
      console.error(`Failed to update setting ${key}:`, error)
    } finally {
      setSaving(false)
    }
  }

  // Update default model
  const updateDefaultModel = async (modelId: string) => {
    try {
      setSaving(true)
      await adminApi.updateDefaultLLMModel(modelId)
      setDefaultModel(modelId)
    } catch (error) {
      console.error('Failed to update default model:', error)
    } finally {
      setSaving(false)
    }
  }

  // Update default tool model
  const updateDefaultToolModel = async (modelId: string) => {
    try {
      setSaving(true)
      await adminApi.updateDefaultToolModel(modelId)
      setDefaultToolModel(modelId)
    } catch (error) {
      console.error('Failed to update default tool model:', error)
    } finally {
      setSaving(false)
    }
  }

  // Update default image model (text-to-image)
  const updateDefaultImageModel = async (modelId: string) => {
    try {
      setSaving(true)
      await adminApi.updateDefaultImageModel(modelId)
      setDefaultImageModel(modelId)
    } catch (error) {
      console.error('Failed to update default image model:', error)
    } finally {
      setSaving(false)
    }
  }

  // Update default image-to-image model
  const updateDefaultImageToImageModel = async (modelId: string) => {
    try {
      setSaving(true)
      await adminApi.updateDefaultImageToImageModel(modelId)
      setDefaultImageToImageModel(modelId)
    } catch (error) {
      console.error('Failed to update default image-to-image model:', error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Platform Settings</h1>
          <p className="text-muted-foreground">
            Configure platform-wide settings and preferences
          </p>
        </div>
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-blue-600" />
            <CardTitle>General Settings</CardTitle>
          </div>
          <CardDescription>
            Basic platform configuration and behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invite Only Setting */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Invite Only Mode</Label>
              <div className="text-sm text-muted-foreground">
                {settings.inviteOnly?.description}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings.inviteOnly?.value === 'true' ? 'default' : 'secondary'}>
                {settings.inviteOnly?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('inviteOnly', settings.inviteOnly?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings.inviteOnly?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* Default Invite Quota */}
          <div className="space-y-2">
            <Label htmlFor="defaultInviteQuota">Default Invite Quota</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings.defaultInviteQuota?.description}
            </div>
            <div className="flex items-center space-x-2">
              <Input
                id="defaultInviteQuota"
                type="number"
                min="0"
                max="100"
                value={settings.defaultInviteQuota?.value || '5'}
                onChange={(e) => updateSetting('defaultInviteQuota', e.target.value)}
                className="w-32"
                disabled={saving}
              />
              <span className="text-sm text-muted-foreground">invites per new user</span>
            </div>
          </div>

          {/* Agent Max Loops */}
          <div className="space-y-2">
            <Label htmlFor="agentMaxLoops">Agent Max Loops</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings.agentMaxLoops?.description || 'Maximum number of continuation loops an agent can execute before stopping'}
            </div>
            <div className="flex items-center space-x-2">
              <Input
                id="agentMaxLoops"
                type="number"
                min="1"
                max="100"
                value={settings.agentMaxLoops?.value || '20'}
                onChange={(e) => updateSetting('agentMaxLoops', e.target.value)}
                className="w-32"
                disabled={saving}
              />
              <span className="text-sm text-muted-foreground">loops per agent execution</span>
            </div>
          </div>

          {/* OpenRouter Usage Tracking */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">OpenRouter Usage Tracking</Label>
              <div className="text-sm text-muted-foreground">
                {settings.openRouterIncludeUsage?.description || 'Enable OpenRouter usage tracking to get accurate cost data (adds ~200-300ms latency)'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings.openRouterIncludeUsage?.value === 'true' ? 'default' : 'secondary'}>
                {settings.openRouterIncludeUsage?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('openRouterIncludeUsage', settings.openRouterIncludeUsage?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings.openRouterIncludeUsage?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* Native (MCP) Tool Calls */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Native Tool Calls (MCP)</Label>
              <div className="text-sm text-muted-foreground">
                {settings.enableNativeToolCalls?.description || 'Use native MCP/tool-use calls instead of inline XML where the model supports it (falls back to XML otherwise)'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings.enableNativeToolCalls?.value === 'true' ? 'default' : 'secondary'}>
                {settings.enableNativeToolCalls?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('enableNativeToolCalls', settings.enableNativeToolCalls?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings.enableNativeToolCalls?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* What each active model actually resolves to. The toggle above is
              only the platform gate: a model without function calling, or
              without tool streaming on a turn that has streaming tools
              (artifact writes, so nearly every turn), still runs XML. */}
          {wireFormats && wireFormats.length > 0 && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="text-sm font-medium">Resolved tool wire format per model</div>
              <div className="text-xs text-muted-foreground">
                For a turn with streaming tools (artifact writes), which is nearly every agent turn. Per-conversation overrides are not shown.
              </div>
              <div className="divide-y">
                {wireFormats.map((m) => {
                  const primary = m.withStreamingTools
                  const other = m.withoutStreamingTools
                  return (
                    <div key={m.id} className="flex items-center justify-between gap-4 py-1.5">
                      <div className="min-w-0">
                        <div className="truncate text-sm">{m.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {m.provider} · {m.serviceIdentifier}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {primary.format !== other.format && (
                          <span className="text-xs text-muted-foreground">
                            {other.format === 'mcp' ? 'native' : 'xml'} without streaming tools
                          </span>
                        )}
                        {primary.reason !== 'native' && (
                          <span className="text-xs text-muted-foreground">
                            {WIRE_FORMAT_REASON_LABEL[primary.reason]}
                            {primary.reason === 'no-tool-streaming' && primary.toolStreamingSource === 'inferred' ? ' (inferred from name)' : ''}
                            {primary.reason === 'no-function-calling' && primary.toolCallsSource === 'inferred' ? ' (inferred from name)' : ''}
                          </span>
                        )}
                        <Badge variant={primary.format === 'mcp' ? 'default' : 'secondary'}>
                          {primary.format === 'mcp' ? 'native' : 'xml'}
                        </Badge>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Agent Runtime */}
          <div className="space-y-2">
            <Label className="text-base">Agent Runtime</Label>
            <div className="text-sm text-muted-foreground">
              {settings.agentRuntime?.description || 'How agent turns execute: Ponder Loop (our orchestration) or Agent SDK Loop (Claude Agent SDK owns the loop + context; Claude models only).'}
            </div>
            <Select
              value={settings.agentRuntime?.value || 'ponder'}
              onValueChange={(v) => updateSetting('agentRuntime', v)}
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Select agent runtime" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ponder">Ponder Loop (default)</SelectItem>
                <SelectItem value="sdk-loop">Agent SDK Loop (Claude only)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Avatar drawing effort (new-agent flow) */}
          <div className="space-y-2">
            <Label className="text-base">Avatar Drawing Effort</Label>
            <div className="text-sm text-muted-foreground">
              {settings.avatarDrawReasoningEffort?.description ||
                "Reasoning effort for drawing a new agent's avatar. Low draws in about 7 s; the model default thinks for about 25 s first."}
            </div>
            <Select
              value={settings.avatarDrawReasoningEffort?.value || 'low'}
              onValueChange={(v) => updateSetting('avatarDrawReasoningEffort', v)}
              disabled={!settings.avatarDrawReasoningEffort}
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Select drawing effort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low (fastest, recommended)</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="default">Model default</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Model vision (docs/model-vision-spec.md §3): every size, detail level,
          encoding and budget the image path uses. Rows are seeded by the global
          migration; a key with no row uses its code default. Saved on blur. */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Image className="h-5 w-5" />
            <CardTitle>Model Vision</CardTitle>
          </div>
          <CardDescription>
            Images shown to agents: avatar and canvas renders, uploaded images. Sizes are the longest edge in pixels; detail is low, high or auto; tool calls and smart actions can ask for more, never past the caps.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.values(settings)
            .filter((s) => s.key.startsWith('vision'))
            .sort((a, b) => a.key.localeCompare(b.key))
            .map((s) => (
              <div key={s.key} className="grid grid-cols-[16rem_12rem_1fr] items-center gap-3">
                <Label htmlFor={s.key} className="font-mono text-xs">{s.key}</Label>
                <Input
                  id={s.key}
                  defaultValue={s.value ?? ''}
                  onBlur={(e) => {
                    if (e.target.value !== (s.value ?? '')) updateSetting(s.key, e.target.value.trim())
                  }}
                  className="h-8"
                  disabled={saving}
                />
                <span className="text-sm text-muted-foreground">{s.description}</span>
              </div>
            ))}
          {!Object.keys(settings).some((k) => k.startsWith('vision')) && (
            <div className="text-sm text-muted-foreground">
              No vision settings rows yet: run the global migrations (20260927000002-add-model-vision-settings). The code defaults apply meanwhile.
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Prompts */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-orange-600" />
            <CardTitle>System Prompts</CardTitle>
          </div>
          <CardDescription>
            Configure global system prompts for agent behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Global System Prompt */}
          <div className="space-y-2">
            <Label htmlFor="globalSystemPrompt">Global System Prompt</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings.globalSystemPrompt?.description || 'Global system prompt used for all agents'}
            </div>
            <Textarea
              id="globalSystemPrompt"
              value={globalSystemPromptValue}
              onChange={(e) => {
                setGlobalSystemPromptValue(e.target.value)
                setHasUnsavedChanges(e.target.value !== (settings.globalSystemPrompt?.value || ''))
              }}
              className="min-h-[150px] font-mono text-sm"
              disabled={saving}
              placeholder="Enter the global system prompt..."
            />
            <div className="flex items-center justify-between">
              <div>
                {hasUnsavedChanges && (
                  <p className="text-sm text-amber-600">
                    You have unsaved changes
                  </p>
                )}
              </div>
              <Button
                size="sm"
                onClick={async () => {
                  await updateSetting('globalSystemPrompt', globalSystemPromptValue)
                  setHasUnsavedChanges(false)
                }}
                disabled={saving || !hasUnsavedChanges}
              >
                Save System Prompt
              </Button>
            </div>
            {settings.globalSystemPrompt?.updatedAt && (
              <p className="text-xs text-muted-foreground">
                Last updated: {new Date(settings.globalSystemPrompt.updatedAt).toLocaleString()}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* LLM Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Brain className="h-5 w-5 text-purple-600" />
            <CardTitle>LLM Configuration</CardTitle>
          </div>
          <CardDescription>
            Configure available language models and default selection
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Default Model Selection */}
          <div className="space-y-2">
            <Label htmlFor="defaultModel">Default Model</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings.defaultModel?.description}
            </div>
            <Select value={defaultModel} onValueChange={updateDefaultModel}>
              <SelectTrigger>
                <SelectValue placeholder="Select default model" />
              </SelectTrigger>
              <SelectContent>
                {availableModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name} ({model.provider})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Default Tool Model Selection */}
          <div className="space-y-2">
            <Label htmlFor="defaultToolModel">Default Tool Model</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings.defaultToolModel?.description || 'Fast model used for tool calls and context building'}
            </div>
            <Select value={defaultToolModel} onValueChange={updateDefaultToolModel}>
              <SelectTrigger>
                <SelectValue placeholder="Select default tool model" />
              </SelectTrigger>
              <SelectContent>
                {availableToolModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.name} ({model.provider})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </CardContent>
      </Card>

      {/* Image Generation Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Image className="h-5 w-5 text-pink-600" />
            <CardTitle>Image Generation Configuration</CardTitle>
          </div>
          <CardDescription>
            Configure default models for image generation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Default Text-to-Image Model Selection */}
          <div className="space-y-2">
            <Label htmlFor="defaultImageModel">Default Text-to-Image Model</Label>
            <div className="text-sm text-muted-foreground mb-2">
              Model used for generating images from text prompts
            </div>
            <Select value={defaultImageModel} onValueChange={updateDefaultImageModel} disabled={saving || availableImageModels.length === 0}>
              <SelectTrigger>
                <SelectValue placeholder={availableImageModels.length === 0 ? 'No models available' : 'Select default model'} />
              </SelectTrigger>
              <SelectContent>
                {availableImageModels.map((model) => (
                  <SelectItem key={model.serviceIdentifier} value={model.serviceIdentifier}>
                    {model.displayName} (${model.costUsdPerUnit ? Number(model.costUsdPerUnit).toFixed(4) : 'N/A'}/image)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Default Image-to-Image Model Selection */}
          <div className="space-y-2">
            <Label htmlFor="defaultImageToImageModel">Default Image-to-Image Model</Label>
            <div className="text-sm text-muted-foreground mb-2">
              Model used for transforming existing images (style transfer, edits)
            </div>
            <Select value={defaultImageToImageModel} onValueChange={updateDefaultImageToImageModel} disabled={saving || availableImageModels.length === 0}>
              <SelectTrigger>
                <SelectValue placeholder={availableImageModels.length === 0 ? 'No models available' : 'Select default model'} />
              </SelectTrigger>
              <SelectContent>
                {availableImageModels.map((model) => (
                  <SelectItem key={model.serviceIdentifier} value={model.serviceIdentifier}>
                    {model.displayName} (${model.costUsdPerUnit ? Number(model.costUsdPerUnit).toFixed(4) : 'N/A'}/image)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Available Models Info */}
          {availableImageModels.length > 0 && (
            <div className="pt-4 border-t">
              <Label className="text-sm font-medium">Available Models</Label>
              <div className="mt-2 space-y-2">
                {availableImageModels.map((model) => (
                  <div key={model.serviceIdentifier} className="flex items-center justify-between text-sm">
                    <span>{model.displayName}</span>
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline">
                        ${model.costUsdPerUnit ? Number(model.costUsdPerUnit).toFixed(4) : 'N/A'}/image
                      </Badge>
                      <Badge variant="secondary">
                        {model.creditsPerAction} credits
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image Styles Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Palette className="h-5 w-5 text-purple-600" />
            <CardTitle>Image Styles</CardTitle>
          </div>
          <CardDescription>
            Configure image generation styles that users can select for their images
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {imageStyles.length === 0 ? (
            <div className="text-sm text-muted-foreground">No image styles configured</div>
          ) : (
            <div className="space-y-4">
              {imageStyles.map((style) => (
                <div key={style.id} className="border rounded-lg p-4">
                  {editingStyle?.id === style.id ? (
                    // Edit mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input
                            value={editingStyle.name}
                            onChange={(e) => setEditingStyle({ ...editingStyle, name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Preferred Model (optional)</Label>
                          <Select
                            value={editingStyle.preferredModel || '__default__'}
                            onValueChange={(value) => setEditingStyle({ ...editingStyle, preferredModel: value === '__default__' ? undefined : value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Use platform default" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__default__">Use platform default</SelectItem>
                              {availableImageModels.map((model) => (
                                <SelectItem key={model.serviceIdentifier} value={model.serviceIdentifier}>
                                  {model.displayName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={editingStyle.description || ''}
                          onChange={(e) => setEditingStyle({ ...editingStyle, description: e.target.value })}
                          placeholder="Brief description of the style"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Style Prompt</Label>
                        <Textarea
                          value={editingStyle.stylePrompt}
                          onChange={(e) => setEditingStyle({ ...editingStyle, stylePrompt: e.target.value })}
                          placeholder="Prompt prefix that defines this style"
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Negative Prompt (optional)</Label>
                        <Textarea
                          value={editingStyle.negativePrompt || ''}
                          onChange={(e) => setEditingStyle({ ...editingStyle, negativePrompt: e.target.value })}
                          placeholder="Elements to avoid in generated images"
                          rows={2}
                        />
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`enabled-${style.id}`}
                            checked={editingStyle.enabled}
                            onChange={(e) => setEditingStyle({ ...editingStyle, enabled: e.target.checked })}
                            className="h-4 w-4"
                          />
                          <Label htmlFor={`enabled-${style.id}`}>Enabled</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingStyle(null)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={async () => {
                              setSaving(true)
                              try {
                                await adminApi.updateImageStyle(editingStyle.id, editingStyle)
                                setImageStyles(styles =>
                                  styles.map(s => s.id === editingStyle.id ? editingStyle : s)
                                )
                                setEditingStyle(null)
                              } catch (error) {
                                console.error('Failed to update style:', error)
                              } finally {
                                setSaving(false)
                              }
                            }}
                            disabled={saving}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium">{style.name}</h4>
                          <Badge variant={style.enabled ? 'default' : 'secondary'}>
                            {style.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                          {style.preferredModel && (
                            <Badge variant="outline">
                              {availableImageModels.find(m => m.serviceIdentifier === style.preferredModel)?.displayName || style.preferredModel}
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingStyle({ ...style })}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </div>
                      {style.description && (
                        <p className="text-sm text-muted-foreground">{style.description}</p>
                      )}
                      <div className="text-sm">
                        <span className="font-medium">Style prompt: </span>
                        <span className="text-muted-foreground">{style.stylePrompt.substring(0, 100)}{style.stylePrompt.length > 100 ? '...' : ''}</span>
                      </div>
                      {style.negativePrompt && (
                        <div className="text-sm">
                          <span className="font-medium">Negative: </span>
                          <span className="text-muted-foreground">{style.negativePrompt.substring(0, 80)}{style.negativePrompt.length > 80 ? '...' : ''}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-green-600" />
            <CardTitle>Search Configuration</CardTitle>
          </div>
          <CardDescription>
            Configure web search functionality and providers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search Enabled */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Search Enabled</Label>
              <div className="text-sm text-muted-foreground">
                {settings['search.enabled']?.description || 'Enable or disable search functionality globally'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings['search.enabled']?.value === 'true' ? 'default' : 'secondary'}>
                {settings['search.enabled']?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('search.enabled', settings['search.enabled']?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings['search.enabled']?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* Max Results */}
          <div className="space-y-2">
            <Label htmlFor="searchMaxResults">Max Search Results</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings['search.maxResults']?.description || 'Default maximum number of search results'}
            </div>
            <div className="flex items-center space-x-2">
              <Input
                id="searchMaxResults"
                type="number"
                min="1"
                max="20"
                value={settings['search.maxResults']?.value || '5'}
                onChange={(e) => updateSetting('search.maxResults', e.target.value)}
                className="w-32"
                disabled={saving}
              />
              <span className="text-sm text-muted-foreground">results per search</span>
            </div>
          </div>

          {/* Daily Cost Budget */}
          <div className="space-y-2">
            <Label htmlFor="searchDailyBudget">Daily Cost Budget</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings['search.costBudget.daily']?.description || 'Daily cost budget for search operations in USD'}
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">$</span>
              <Input
                id="searchDailyBudget"
                type="number"
                min="0"
                step="0.01"
                value={settings['search.costBudget.daily']?.value || '10.00'}
                onChange={(e) => updateSetting('search.costBudget.daily', e.target.value)}
                className="w-32"
                disabled={saving}
              />
              <span className="text-sm text-muted-foreground">USD per day</span>
            </div>
          </div>

          {/* Per Conversation Cost Budget */}
          <div className="space-y-2">
            <Label htmlFor="searchConversationBudget">Per Conversation Budget</Label>
            <div className="text-sm text-muted-foreground mb-2">
              {settings['search.costBudget.perConversation']?.description || 'Maximum search cost per conversation in USD'}
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">$</span>
              <Input
                id="searchConversationBudget"
                type="number"
                min="0"
                step="0.01"
                value={settings['search.costBudget.perConversation']?.value || '0.50'}
                onChange={(e) => updateSetting('search.costBudget.perConversation', e.target.value)}
                className="w-32"
                disabled={saving}
              />
              <span className="text-sm text-muted-foreground">USD per conversation</span>
            </div>
          </div>

          {/* Cache Settings */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Search Result Caching</Label>
              <div className="text-sm text-muted-foreground">
                {settings['search.enableCaching']?.description || 'Enable caching of search results'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings['search.enableCaching']?.value === 'true' ? 'default' : 'secondary'}>
                {settings['search.enableCaching']?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('search.enableCaching', settings['search.enableCaching']?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings['search.enableCaching']?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* Provider Status */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Exa Search Provider</Label>
                <div className="text-sm text-muted-foreground">
                  {settings['search.providers.exa.enabled']?.value === 'true' 
                    ? 'Provider is enabled (API key configured in .env)'
                    : 'Provider is disabled'}
                </div>
              </div>
              <Badge variant={settings['search.providers.exa.enabled']?.value === 'true' ? 'default' : 'secondary'}>
                {settings['search.providers.exa.enabled']?.value === 'true' ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Encryption & Security */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Lock className="h-5 w-5 text-amber-600" />
            <CardTitle>Encryption & Security</CardTitle>
          </div>
          <CardDescription>
            Configure encryption at rest for conversation and artifact data (requires AWS KMS)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Conversation Message Encryption */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Conversation Message Encryption</Label>
              <div className="text-sm text-muted-foreground">
                {settings['encryption.conversationEnabled']?.description || 'Enable encryption at rest for conversation messages (requires AWS KMS)'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings['encryption.conversationEnabled']?.value === 'true' ? 'default' : 'secondary'}>
                {settings['encryption.conversationEnabled']?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('encryption.conversationEnabled', settings['encryption.conversationEnabled']?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings['encryption.conversationEnabled']?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* Artifact Data Encryption */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Artifact Data Encryption</Label>
              <div className="text-sm text-muted-foreground">
                {settings['encryption.artifactEnabled']?.description || 'Enable encryption at rest for artifact data (requires AWS KMS)'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant={settings['encryption.artifactEnabled']?.value === 'true' ? 'default' : 'secondary'}>
                {settings['encryption.artifactEnabled']?.value === 'true' ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateSetting('encryption.artifactEnabled', settings['encryption.artifactEnabled']?.value === 'true' ? 'false' : 'true')}
                disabled={saving}
              >
                {settings['encryption.artifactEnabled']?.value === 'true' ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-green-600" />
            <CardTitle>System Information</CardTitle>
          </div>
          <CardDescription>
            Current platform configuration and status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Total Settings</Label>
                <p className="text-2xl font-bold">{Object.keys(settings).length}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Available LLM Models</Label>
                <p className="text-2xl font-bold">{availableModels.length}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Available Image Models</Label>
                <p className="text-2xl font-bold">{availableImageModels.length}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Default LLM Model</Label>
                <p className="text-lg font-medium">
                  {availableModels.find(m => m.id === defaultModel)?.name || 'None selected'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">Default Tool Model</Label>
                <p className="text-lg font-medium">
                  {availableToolModels.find(m => m.id === defaultToolModel)?.name || 'None selected'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">Invite Mode</Label>
                <div className="flex items-center space-x-2">
                  {settings.inviteOnly?.value === 'true' ? (
                    <Badge variant="default">Invite Only</Badge>
                  ) : (
                    <Badge variant="secondary">Open Registration</Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Default Text-to-Image</Label>
                <p className="text-lg font-medium">
                  {availableImageModels.find(m => m.serviceIdentifier === defaultImageModel)?.displayName || 'None selected'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">Default Image-to-Image</Label>
                <p className="text-lg font-medium">
                  {availableImageModels.find(m => m.serviceIdentifier === defaultImageToImageModel)?.displayName || 'None selected'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default PlatformSettingsPage